import { createActionsHook, createHook, createStore } from 'react-sweet-state';
import type {
	Action,
	BoundActions,
	HookActionsFunction,
	HookFunction,
	Store,
} from 'react-sweet-state';
import {
	type SDKUnsupportedReasons,
	type createInstance as CreateLoomInstanceType,
} from '@loomhq/record-sdk';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { expVal } from '@confluence/feature-experiments';

import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';

import { INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC } from './perf.config.ts';
import { getAuthToken } from './loomAuthData.ts';
import { getLoomInstanceDataByEnvironment } from './getLoomInstanceDataByEnvironment.ts';
import {
	type NoRecordingAccessReason,
	getNoRecordingAccessReason,
} from './getNoRecordingAcccessReason.ts';

type InitializedLoomSDK = Awaited<ReturnType<typeof CreateLoomInstanceType>>;

export type StateType = {
	isInitializing: boolean;
	initializedLoomSDK: InitializedLoomSDK | null;
	initializedLoomSDKError: Error | null | undefined;
	_hasLoomSDKInitializationStarted: boolean;
	hasRecordingAccess: boolean;
	noRecordingAccessReason: NoRecordingAccessReason | undefined;
	authTokenError: ApolloErrorInterop | Error | null | undefined;
	isLoomSDKSupportedError: SDKUnsupportedReasons | undefined;
};

type InitSDKParams = {
	cloudId: string;
	environment: SessionDataType['environment'];
	entryPointLocation: string;
};
type InitLoomSDKType = ({ cloudId }: InitSDKParams) => Action<StateType>;
type ActionsType = {
	initLoomSDK: InitLoomSDKType;
};

const initialState: StateType = {
	isInitializing: true,
	initializedLoomSDK: null,
	_hasLoomSDKInitializationStarted: false,
	hasRecordingAccess: false,
	noRecordingAccessReason: undefined,
	authTokenError: undefined,
	isLoomSDKSupportedError: undefined,
	initializedLoomSDKError: null,
};

const initSDK: InitLoomSDKType =
	({ cloudId, environment, entryPointLocation }) =>
	async ({ getState, setState }) => {
		if (getState().initializedLoomSDK || getState()._hasLoomSDKInitializationStarted) {
			// singleton: only want 1 Loom SDK instance
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
			return;
		}
		setState({ _hasLoomSDKInitializationStarted: true });
		INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.mark('loomSdkInitStart');

		try {
			const { isSupported } = await import(
				/* webpackChunkName: "loadable-loomhq-record-sdk-is-supported" */ '@loomhq/record-sdk/is-supported'
			);
			const { supported, error: isLoomSDKSupportedError } = await isSupported();

			if (!supported) {
				setState({ isLoomSDKSupportedError, isInitializing: false });
				INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
				return;
			}
		} catch {
			// If SDK isSupported import/exec fails: treat as incompatible browser
			setState({
				isLoomSDKSupportedError: 'incompatible-browser' as SDKUnsupportedReasons,
				isInitializing: false,
			});
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
			return;
		}

		const { recorderToken, tokenError } = await getAuthToken();
		if (!recorderToken || tokenError) {
			const noRecordingAccessReason = await getNoRecordingAccessReason(tokenError);
			setState({
				isInitializing: false,
				noRecordingAccessReason,
				authTokenError: tokenError,
			});
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
			return;
		}
		setState({ hasRecordingAccess: true });

		try {
			const { createInstance } = await import(
				/* webpackChunkName: "loadable-loomhq-record-sdk" */ '@loomhq/record-sdk'
			);

			const initializedLoomSDK = await createInstance(
				getLoomInstanceDataByEnvironment({
					environment,
					jws: recorderToken,
					siteId: cloudId,
					entryPointName: entryPointLocation,
				}),
			);

			setState({ initializedLoomSDK, isInitializing: false });
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.stop({
				customData: {
					cohort: expVal('cc-loom-recorder-init-as-background-loadable', 'cohort', 'control'),
				},
			});
		} catch (err) {
			setState({
				isInitializing: false,
				initializedLoomSDKError: err as Error,
			});
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
		}
	};

const actions: ActionsType = {
	initLoomSDK: initSDK,
};

export const store: Store<StateType, ActionsType> = createStore<StateType, ActionsType>({
	initialState,
	actions,
	name: 'loomEntryPointGlobalStateStore',
});

export const useLoomEntryPointGlobalState: HookFunction<
	StateType,
	BoundActions<StateType, ActionsType>,
	void
> = createHook(store);

export const useLoomEntryPointGlobalStateActions: HookActionsFunction<
	BoundActions<StateType, ActionsType>
> = createActionsHook(store);
